import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Spinner, Row, Col, ListGroup, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaFileUpload, FaCheck, FaExclamationTriangle, FaChartLine, FaSync, FaSyncAlt } from 'react-icons/fa';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { submitToLender } from '../../api/lenderApi';
import { getAdvancedCriteriaSettings } from '../../api/advancedCriteriaApi';
import { evaluateAdvancedCriteria } from '../../utils/advancedCriteriaEvaluation';
import { evaluateLenderVisibility } from '../../utils/lenderVisibility';
import EnhancedLenderButton from './EnhancedLenderButton';
import EquipmentInformation from './EquipmentInformation';
import ConfirmationModal from './ConfirmationModal';

// Custom styles for form labels
const labelStyle = {
  fontWeight: '600',
  color: '#495057',
  marginBottom: '0.25rem', // Reduced margin between label and input
  fontSize: '0.95rem'
};

/**
 * LenderSubmission Component
 * 
 * Handles the submission of contact and business data to various lenders.
 * Uses provided bank statements and application forms.
 * Shows submission status, results, and error messages.
 * Integrates with the notification system to display toast notifications.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.contactData - Contact information
 * @param {Object} props.opportunityData - Business opportunity data (renamed to businessData internally)
 * @param {Array} props.bankFiles - Array of bank statement files
 * @param {Array} props.appFiles - Array of application document files
 * @param {Array} props.quoteFiles - Array of equipment quote files for equipment financing
 * @param {Array} props.creditFiles - Array of credit files for equipment financing
 * @param {Array} props.assetFormFiles - Array of asset form files for real estate
 * @param {boolean} [props.disabled=false] - Whether submission is disabled
 * @param {string} [props.financingType='working-capital'] - Type of financing ('working-capital', 'equipment-financing', 'sba', or 'real-estate')
 * @param {Array} [props.preloadedLenders=[]] - Pre-filtered lenders for this financing type (eliminates loading delay)
 * @param {boolean} [props.lendersLoading=false] - Whether lenders are still being loaded
 * @param {Object} [props.preloadedAdvancedCriteria=null] - Pre-loaded advanced criteria settings (eliminates loading delay)
 * @param {boolean} [props.advancedCriteriaLoading=false] - Whether advanced criteria settings are still being loaded
 * @returns {JSX.Element} LenderSubmission component
 */
const LenderSubmission = ({ 
  contactData, 
  opportunityData, 
  bankFiles = [], 
  appFiles = [],
  quoteFiles = [],
  creditFiles = [],
  assetFormFiles = [], 
  disabled = false,
  financingType = 'working-capital',
  preloadedLenders = [],
  lendersLoading = false,
  preloadedAdvancedCriteria = null,
  advancedCriteriaLoading = false
}) => {
  // Rename the prop internally for clarity
  const businessData = opportunityData;
  const [eligibilityLoaded, setEligibilityLoaded] = useState(Boolean(preloadedAdvancedCriteria));
  
  // Get the opportunity ID from the URL params
  const { opportunityId } = useParams();
  
  // Access notification context
  const { success, error: notifyError, info, warning } = useNotifications();
  
  // Access auth context for bypass permission
  const { user, refreshUser } = useAuth();
  const canBypassAutoDecline = user?.canBypassAutoDecline || false;
  
  // State for lenders (now using preloaded data)
  // Initialize with preloadedLenders to avoid initial empty state flash
  const [lenders, setLenders] = useState(preloadedLenders);

  // Refresh user data on mount to get latest canBypassAutoDecline value
  useEffect(() => {
    console.log('LenderSubmission: Refreshing user data to get latest bypass permission...');
    refreshUser();
  }, [refreshUser]);

  // Log the opportunity ID when the component initializes
  useEffect(() => {
    console.log(`LenderSubmission component initialized with opportunityId: ${opportunityId}, financingType: ${financingType}`);
    console.log(`User canBypassAutoDecline: ${canBypassAutoDecline}`);
  }, [opportunityId, financingType, canBypassAutoDecline]);
  
  // Use preloaded lenders when they become available or financing type changes
  useEffect(() => {
    console.log(`Using preloaded lenders for financing type: ${financingType}`);
    console.log(`Received ${preloadedLenders.length} preloaded lenders`);
    console.log(`Lenders loading: ${lendersLoading}`);
    
    // Only update lenders if we're not loading or if we have actual lenders
    // This prevents the brief "no lenders" flash when loading finishes but props haven't updated yet
    if (!lendersLoading || preloadedLenders.length > 0) {
      setLenders(preloadedLenders);
    }
    
    // Reset submission states when changing financing type
    setResults({});
    setErrors({});
    setErrorDetails({});
    setSubmittingLenders({});
    setResult(null);
    setError(null);
    setCurrentLender(null);
  }, [financingType, preloadedLenders, lendersLoading]);
  
  // State for submission status
  const [submitting, setSubmitting] = useState(false);
  const [currentLender, setCurrentLender] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // NEW: State for tracking multiple submissions
  const [submittingLenders, setSubmittingLenders] = useState({});
  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});
  const [errorDetails, setErrorDetails] = useState({});
  
  // State for advanced lender match functionality (replaces old system)
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [lastScoreUpdate, setLastScoreUpdate] = useState(null);
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  
  // State for advanced criteria (conditional chaining)
  const [advancedCriteriaEnabled, setAdvancedCriteriaEnabled] = useState(preloadedAdvancedCriteria?.enabled || false);
  const [advancedCriteria, setAdvancedCriteria] = useState(preloadedAdvancedCriteria?.lenders || []);
  const [advancedMatchScores, setAdvancedMatchScores] = useState({});
  
  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  
  // State for tracking auto-decline status per lender
  const [autoDeclineStatus, setAutoDeclineStatus] = useState({});
  
  // List of field IDs that contain numeric values but are stored as text
  const numericTextFields = [
    'AverageBankBalance',
    'AnnualRevenue',
    'RequestedAmount',
    'ContactOwnershipPercentage',
    'FederalTaxID',
    // Add any other fields that should be treated as numbers
  ];
  
  // Helper function to determine if a field should be treated as numeric
  const isNumericField = (fieldId) => {
    return numericTextFields.includes(fieldId);
  };
  
  // Use preloaded advanced criteria settings or load them if not provided
  useEffect(() => {
    const loadAdvancedCriteria = async () => {
      // If preloaded data is available and not still loading, use it immediately
      if (preloadedAdvancedCriteria && !advancedCriteriaLoading) {
        console.log('📋 Using preloaded advanced criteria settings:', preloadedAdvancedCriteria);
        
        setEligibilityLoaded(true);
        setAdvancedCriteriaEnabled(preloadedAdvancedCriteria.enabled);
        
        if (preloadedAdvancedCriteria.enabled && preloadedAdvancedCriteria.lenders && preloadedAdvancedCriteria.lenders.length > 0) {
          setAdvancedCriteria(preloadedAdvancedCriteria.lenders);
          
          // Calculate advanced scores immediately
          calculateAdvancedMatchScores(preloadedAdvancedCriteria.lenders);
        }
        
        setLoadingMatch(false);
        return;
      }
      
      // If still loading preloaded data, wait
      if (advancedCriteriaLoading) {
        console.log('⏳ Waiting for preloaded advanced criteria...');
        return;
      }
      
      // Fallback: Load from API if no preloaded data available
      try {
        setLoadingMatch(true);
        console.log('📥 Loading advanced criteria settings from API (fallback)...');
        
        const advancedSettings = await getAdvancedCriteriaSettings();
        console.log('📋 Advanced criteria settings loaded:', advancedSettings);
        
        setEligibilityLoaded(true);
        setAdvancedCriteriaEnabled(advancedSettings.enabled);
        
        if (advancedSettings.enabled && advancedSettings.lenders && advancedSettings.lenders.length > 0) {
          setAdvancedCriteria(advancedSettings.lenders);
          
          // Calculate advanced scores
          calculateAdvancedMatchScores(advancedSettings.lenders);
        }
      } catch (err) {
        console.error('Error loading advanced criteria settings:', err);
      } finally {
        setLoadingMatch(false);
      }
    };
    
    loadAdvancedCriteria();
  }, [preloadedAdvancedCriteria, advancedCriteriaLoading]);
  
  // Force recalculation when specific fields are updated through the form
  const forceRecalculateScores = () => {
    if (advancedCriteriaEnabled && advancedCriteria.length > 0) {
      console.log('Forcing recalculation of advanced match scores');
      calculateAdvancedMatchScores(advancedCriteria);
    }
  };
  
  // Get the most current field value, checking the DOM if necessary
  const getCurrentFieldValue = (fieldId) => {
    // Special case for CumulativeOwnershipPercentage - calculate from primary and secondary owner percentages
    if (fieldId === 'CumulativeOwnershipPercentage') {
      // Get primary owner percentage
      const primaryOwnership = normalizeNumericValue(
        businessData?.ContactOwnershipPercentage || 
        document.querySelector('input[name="ContactOwnershipPercentage"], input[id="ContactOwnershipPercentage"]')?.value ||
        0
      );
      
      // Get secondary owner percentage
      const secondaryOwnership = normalizeNumericValue(
        businessData?.Contact2OwnershipPercentage || 
        document.querySelector('input[name="Contact2OwnershipPercentage"], input[id="Contact2OwnershipPercentage"]')?.value ||
        0
      );
      
      // Calculate cumulative ownership
      const cumulativeOwnership = primaryOwnership + secondaryOwnership;
      console.log(`Calculated cumulative ownership: ${primaryOwnership} + ${secondaryOwnership} = ${cumulativeOwnership}`);
      
      return cumulativeOwnership;
    }
    
    // ========================================
    // Virtual Aggregate Fields for Bank Statement Data
    // These compute MIN or MAX across all 4 months for "ALL months must meet X" criteria
    // ========================================
    
    // Helper function to get numeric values from month fields, filtering out null/undefined/empty
    const getMonthValues = (fieldPrefix) => {
      return [1, 2, 3, 4]
        .map(month => businessData?.[`Month${month}${fieldPrefix}`])
        .filter(v => v !== null && v !== undefined && v !== '')
        .map(v => parseFloat(String(v).replace(/[^0-9.-]/g, '')))
        .filter(v => !isNaN(v));
    };
    
    // AllMonthsDeposits - returns MIN of all months (if min >= X, all months >= X)
    if (fieldId === 'AllMonthsDeposits') {
      const values = getMonthValues('Deposits');
      if (values.length === 0) return null;
      const minValue = Math.min(...values);
      console.log(`AllMonthsDeposits: MIN of [${values.join(', ')}] = ${minValue}`);
      return minValue;
    }
    
    // AllMonthsDepositCount - returns MIN of all months (if min >= X, all months >= X)
    if (fieldId === 'AllMonthsDepositCount') {
      const values = getMonthValues('DepositCount');
      if (values.length === 0) return null;
      const minValue = Math.min(...values);
      console.log(`AllMonthsDepositCount: MIN of [${values.join(', ')}] = ${minValue}`);
      return minValue;
    }
    
    // AllMonthsNSFs - returns MAX of all months (if max <= X, no month exceeds X)
    if (fieldId === 'AllMonthsNSFs') {
      const values = getMonthValues('NSFs');
      if (values.length === 0) return null;
      const maxValue = Math.max(...values);
      console.log(`AllMonthsNSFs: MAX of [${values.join(', ')}] = ${maxValue}`);
      return maxValue;
    }
    
    // AllMonthsNegativeDays - returns MAX of all months (if max <= X, no month exceeds X)
    if (fieldId === 'AllMonthsNegativeDays') {
      const values = getMonthValues('NegativeDays');
      if (values.length === 0) return null;
      const maxValue = Math.max(...values);
      console.log(`AllMonthsNegativeDays: MAX of [${values.join(', ')}] = ${maxValue}`);
      return maxValue;
    }
    
    // First check businessData from props
    if (businessData && businessData[fieldId] !== undefined) {
      return businessData[fieldId];
    }
    
    if (contactData && contactData[fieldId] !== undefined) return contactData[fieldId];

    // If not found or potentially stale, check for form input elements with matching ID or name
    try {
      // Look for input by field ID
      const inputElement = document.querySelector(`input[name="${fieldId}"], input[id="${fieldId}"]`);
      if (inputElement) {
        return inputElement.value;
      }
      
      // For select elements
      const selectElement = document.querySelector(`select[name="${fieldId}"], select[id="${fieldId}"]`);
      if (selectElement) {
        return selectElement.value;
      }
    } catch (err) {
      console.error('Error checking DOM for updated field value:', err);
    }
    
    // Fallback to businessData
    return businessData?.[fieldId];
  };
  
  // Evaluate each criterion with the most current data
  const evaluateCriterionWithCurrentData = (criterion) => {
    // Get the most up-to-date field value
    const fieldValue = getCurrentFieldValue(criterion.fieldId);
    return evaluateCriterion(criterion, fieldValue);
  };
  
  // Check if field is a date comparison field (BusinessStartDate or DateOfBirth)
  const isDateComparisonField = (fieldId) => {
    return fieldId === 'BusinessStartDate' || fieldId === 'DateOfBirth';
  };
  
  // Calculate the difference in months between two dates
  const getMonthDifference = (dateValue) => {
    if (!dateValue) return 0;
    
    try {
      // Parse the date from the YYYY-MM-DD format
      const [year, month, day] = String(dateValue).split('-').map(num => parseInt(num, 10));
      
      // Validate date components
      if (isNaN(year) || isNaN(month) || isNaN(day) || 
          year < 1900 || year > 2100 || 
          month < 1 || month > 12 || 
          day < 1 || day > 31) {
        console.error(`Invalid date format: ${dateValue}`);
        return 0;
      }
      
      // Create date objects
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed in JS
      const currentDate = new Date();
      
      // Additional validation: ensure the date object was created correctly
      if (dateObj.toString() === 'Invalid Date') {
        console.error(`Could not create valid date from: ${dateValue}`);
        return 0;
      }
      
      // Calculate difference in months
      const monthDiff = 
        (currentDate.getFullYear() - dateObj.getFullYear()) * 12 + 
        (currentDate.getMonth() - dateObj.getMonth());
      
      console.log(`Date difference calculation: 
        Date value: ${dateValue}
        Parsed date: ${dateObj.toISOString()}
        Current date: ${currentDate.toISOString()}
        Difference in months: ${monthDiff}`);
      
      return monthDiff;
    } catch (err) {
      console.error('Error calculating month difference:', err);
      return 0;
    }
  };
  
  // Calculate advanced match scores with conditional criteria (replaces old system)
  const calculateAdvancedMatchScores = (lenderAdvancedCriteria) => {
    console.log('🔄 Calculating advanced match scores');
    const scores = {};
    
    lenderAdvancedCriteria.forEach(lender => {
      
      // Skip if no criteria defined - mark as having no criteria
      const hasSimple = lender.simpleCriteria && lender.simpleCriteria.length > 0;
      const hasConditional = lender.conditionalRules && lender.conditionalRules.filter(r => r.enabled).length > 0;
      
      if (!hasSimple && !hasConditional) {
        // Mark this lender as having NO criteria (null-like state)
        scores[lender.lenderId] = {
          score: 0,
          simpleMatched: [],
          simpleMissed: [],
          simpleIncomplete: [],
          conditionalMatched: [],
          conditionalMissed: [],
          conditionalIncomplete: [],
          disqualified: false,
          autoDecline: false,
          totalWeight: 0,  // Key indicator - no criteria defined
          hasCriteria: false
        };
        return;
      }
      
      // Use the evaluation utility
      if (hasConditional) {
        console.log(`📊 Evaluating lender ${lender.name} with CONDITIONAL rules:`, {
          simpleCriteria: lender.simpleCriteria,
          conditionalRulesCount: lender.conditionalRules.length,
          conditionalRules: JSON.stringify(lender.conditionalRules, null, 2)
        });
      }
      
      const result = evaluateAdvancedCriteria(
        lender,
        getCurrentFieldValue,
        evaluateCriterion
      );
      
      if (hasConditional) {
        console.log(`📊 Result for ${lender.name}:`, {
          score: result.score,
          simpleMatched: result.simpleMatched?.length || 0,
          simpleMissed: result.simpleMissed?.length || 0,
          conditionalMatched: result.conditionalMatched?.length || 0,
          conditionalMissed: result.conditionalMissed?.length || 0,
          ruleResults: result.ruleResults
        });
      }
      
      scores[lender.lenderId] = result;
    });
    
    setAdvancedMatchScores(scores);
    
    // Record update time
    setLastScoreUpdate(new Date());
    setShowUpdateAlert(true);
    setTimeout(() => setShowUpdateAlert(false), 3000);
    
    return scores;
  };
  
  // Recalculate advanced scores when business data changes
  useEffect(() => {
    if (advancedCriteriaEnabled && advancedCriteria.length > 0 && businessData) {
      console.log('Business data changed, recalculating advanced match scores');
      const timer = setTimeout(() => {
        calculateAdvancedMatchScores(advancedCriteria);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [businessData, contactData, advancedCriteriaEnabled, advancedCriteria]);
  
  // Get effective match score for a lender - uses advanced criteria only
  const getEffectiveMatchScore = (lenderId) => {
    const visibility = getLenderVisibility(lenderId);
    if (visibility.result) {
      const result = visibility.result;
      return {
        score: result.score,
        matched: [...result.simpleMatched, ...result.conditionalMatched],
        missed: [...result.simpleMissed, ...result.conditionalMissed],
        incomplete: [...result.simpleIncomplete, ...result.conditionalIncomplete],
        disqualified: result.disqualified,
        autoDecline: result.autoDecline,
        advancedCriteriaResults: result
      };
    }
    // If advanced criteria is not enabled, return null (no criteria to show)
    if (!advancedCriteriaEnabled) {
      return null;
    }
    
    // If we don't have scores for this lender, return null
    if (!advancedMatchScores[lenderId]) {
      return null;
    }
    
    const advScore = advancedMatchScores[lenderId];
    
    // If explicitly marked as having no criteria, return null
    if (advScore.hasCriteria === false || advScore.totalWeight === 0) {
      return null;
    }
    
    // Combine simple and conditional results for display
    return {
      score: advScore.score,
      matched: [...(advScore.simpleMatched || []), ...(advScore.conditionalMatched || [])],
      missed: [...(advScore.simpleMissed || []), ...(advScore.conditionalMissed || [])],
      incomplete: [...(advScore.simpleIncomplete || []), ...(advScore.conditionalIncomplete || [])],
      disqualified: advScore.disqualified,
      autoDecline: advScore.autoDecline,
      // Include advanced-specific data for the confirmation modal
      advancedCriteriaResults: advScore
    };
  };
  
  // Normalize a value to a number if possible
  const normalizeNumericValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    
    // Remove any non-numeric characters except decimal point
    // First remove currency symbols, commas, etc.
    const cleanValue = String(value).replace(/[^0-9.-]/g, '');
    const parsedValue = parseFloat(cleanValue);
    
    return isNaN(parsedValue) ? 0 : parsedValue;
  };
  
  // Evaluate if a criterion is met by the field value
  const evaluateCriterion = (criterion, fieldValue) => {
    // If field value is undefined or null, criterion is not met
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      console.log(`Field value is empty for ${criterion.fieldId}`);
      return false;
    }
    
    const { fieldId, operator, value } = criterion;
    
    // Special case for Industry with "is_not" operator
    if (fieldId === 'Industry' && operator === 'is_not') {
      // value should be an array of excluded industries
      // If it's not an array yet, convert it to one
      const excludedIndustries = Array.isArray(value) ? value : value ? [value] : [];
      
      // Check if the field value is NOT in the excluded industries list
      // Return true if the industry is NOT excluded (meaning the criterion is met)
      const result = !excludedIndustries.includes(String(fieldValue));
      
      console.log(`Industry exclusion check for ${fieldValue}: Excluded industries: [${excludedIndustries.join(', ')}], Result: ${result}`);
      
      return result;
    }
    
    // Special case for State fields with "is_not" operator
    if ((fieldId === 'BusinessState' || fieldId === 'ContactState' || fieldId === 'Contact2State') && operator === 'is_not') {
      // value should be an array of excluded states
      // If it's not an array yet, convert it to one
      const excludedStates = Array.isArray(value) ? value : value ? [value] : [];
      
      // Check if the field value is NOT in the excluded states list
      // Return true if the state is NOT excluded (meaning the criterion is met)
      const result = !excludedStates.includes(String(fieldValue));
      
      console.log(`State exclusion check for ${fieldValue}: Excluded states: [${excludedStates.join(', ')}], Result: ${result}`);
      
      return result;
    }
    
    // Special case for "older_than_months" operator on date fields
    if (isDateComparisonField(fieldId) && operator === 'older_than_months') {
      const monthsOld = getMonthDifference(fieldValue);
      const requiredMonths = parseInt(value, 10);
      
      console.log(`Date age comparison for ${fieldId}: ${monthsOld} months old vs required ${requiredMonths} months`);
      
      return monthsOld >= requiredMonths;
    }
    
    // Special handling for numeric text fields
    const isNumeric = isNumericField(fieldId) || 
                     (operator === '>' || operator === '>=' || operator === '<' || operator === '<=');
    
    if (isNumeric) {
      // Normalize both values for comparison
      const numFieldValue = normalizeNumericValue(fieldValue);
      const numCriterionValue = normalizeNumericValue(value);
      
      console.log(`Numeric comparison for ${fieldId}: ${numFieldValue} ${operator} ${numCriterionValue}`);
      
      // Compare as numbers
      switch (operator) {
        case '>':
          return numFieldValue > numCriterionValue;
        case '>=':
          return numFieldValue >= numCriterionValue;
        case '=':
          return numFieldValue === numCriterionValue;
        case '<=':
          return numFieldValue <= numCriterionValue;
        case '<':
          return numFieldValue < numCriterionValue;
        default:
          // For non-comparison operators on numeric fields
          if (operator === 'contains') {
            return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
          }
          return false;
      }
    }
    
    // Handle text operators for non-numeric fields
    switch (operator) {
      case '=':
        return String(fieldValue) === String(value);
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      default:
        return false;
    }
  };
  
  // Visibility and scoring share the saved advanced criteria and current form values.
  const getLenderVisibility = lenderId => {
    if (!eligibilityLoaded || advancedCriteriaLoading) return { visible: false };
    const criteria = advancedCriteria.find(item => item.lenderId === lenderId &&
      (!item.financingType || item.financingType === financingType));
    return evaluateLenderVisibility(criteria, advancedCriteriaEnabled, getCurrentFieldValue, evaluateCriterion);
  };
  const isLenderVisible = lenderId => getLenderVisibility(lenderId).visible;

  // Equipment information state
  const [equipmentItems, setEquipmentItems] = useState([]);
  
  // Clear equipment items from local storage on component mount
  useEffect(() => {
    if (financingType === 'equipment-financing' && opportunityId) {
      // Clear any previously saved equipment items
      localStorage.removeItem(`equipmentItems-${opportunityId}`);
      console.log(`Cleared equipment items from localStorage for opportunity ${opportunityId}`);
    }
  }, [financingType, opportunityId]);

  // Check auto-decline status for all lenders using advanced criteria
  // This now runs synchronously since we use preloaded lender data
  useEffect(() => {
    if (!lenders || lenders.length === 0 || !advancedCriteriaEnabled) {
      return;
    }

    const newAutoDeclineStatus = {};
    
    for (const lender of lenders) {
      try {
        const status = checkAutoDecline(lender.id);
        newAutoDeclineStatus[lender.id] = status;
      } catch (error) {
        console.warn(`Error checking auto-decline for ${lender.name}:`, error);
        newAutoDeclineStatus[lender.id] = { isAutoDecline: false, lenderAllowsOverride: true };
      }
    }
    
    setAutoDeclineStatus(newAutoDeclineStatus);
  }, [lenders, advancedCriteriaEnabled, advancedMatchScores, businessData]);

  // Check if a lender should be auto-declined using advanced criteria
  // Returns object with isAutoDecline and lenderAllowsOverride
  // Now uses preloaded lender data instead of making API calls
  const checkAutoDecline = (lenderId) => {
    try {
      // Use advanced criteria match scores for ALL lenders
      const matchScore = getEffectiveMatchScore(lenderId);
      
      // If no criteria defined, not auto-decline
      if (!matchScore) {
        return { isAutoDecline: false, lenderAllowsOverride: true };
      }
      
      const isAutoDecline = matchScore.autoDecline || matchScore.disqualified;
      
      if (!isAutoDecline) {
        return { isAutoDecline: false, lenderAllowsOverride: true };
      }
      
      // Use preloaded lender data to check if override is allowed (no API call needed)
      const lender = lenders.find(l => l.id === lenderId);
      const lenderAllowsOverride = lender?.allowAutoDeclineOverride !== false; // Default to true
      
      return { isAutoDecline: true, lenderAllowsOverride };
    } catch (error) {
      console.warn('Error checking auto-decline status:', error);
      return { isAutoDecline: false, lenderAllowsOverride: true };
    }
  };

  // Confirmation modal handlers
  const handleConfirmSubmission = async () => {
    console.log('✅ User confirmed submission for:', confirmationData?.lenderName);
    setShowConfirmationModal(false);
    if (confirmationData) {
      await performSubmission(confirmationData.lenderId, confirmationData.submissionType);
    }
  };

  const handleCancelConfirmation = () => {
    console.log('❌ User cancelled submission for:', confirmationData?.lenderName);
    setShowConfirmationModal(false);
    setConfirmationData(null);
  };
  
  // Save equipment items to local storage only after submission
  const saveEquipmentItemsToLocalStorage = (items) => {
    if (financingType === 'equipment-financing' && opportunityId) {
      localStorage.setItem(`equipmentItems-${opportunityId}`, JSON.stringify(items));
      console.log(`Saved ${items.length} equipment items to localStorage for opportunity ${opportunityId}`);
    }
  };
  
  /**
   * Check if lender requires confirmation and show modal or proceed with submission
   * Now checks auto-decline for ALL lenders (universal auto-decline)
   * 
   * @param {string} lenderId - ID of the lender to submit to
   * @param {string} [submissionType='api'] - Type of submission ('api' or 'email')
   */
  const handleSubmitToLender = async (lenderId, submissionType = 'api') => {
    if (!isLenderVisible(lenderId)) return;
    // Get lender info
    const lender = lenders.find(l => l.id === lenderId);
    
    // Safety check - if we can't find the lender, log an error and return
    if (!lender) {
      console.error(`No lender found with ID: ${lenderId}`);
      notifyError(`Error: Lender with ID "${lenderId}" not found`, 'Submission Failed', 5000);
      return;
    }
    
    // Get the effective match score from advanced criteria (for ALL lenders now)
    const matchData = getEffectiveMatchScore(lenderId);
    
    if (matchData) {
      console.log(`📊 Using advanced criteria for ${lender.name} with score: ${matchData.score}%`);
    } else {
      console.log(`⚠️ No criteria found for lender ${lender.name} (${lenderId})`);
    }
    
    // Check for auto-decline: if any required criteria are missed
    if (matchData && matchData.autoDecline) {
      // Use preloaded lender data to check if override is allowed
      const lenderAllowsOverride = lender.allowAutoDeclineOverride !== false;
      
      // Check if user can proceed:
      // 1. If lender allows override → ANY user can proceed with warning
      // 2. If lender doesn't allow override → Only users with bypass can proceed
      
      if (lenderAllowsOverride) {
        // Lender allows override - show warning modal for ALL users
        console.log('⚠️ Auto-decline detected but lender allows override - showing warning modal');
        setConfirmationData({
          lenderId,
          lenderName: lender.name,
          submissionType,
          matchData,
          isAutoDeclineBypass: canBypassAutoDecline,  // Flag to show extra warning if user has bypass
          lenderNormallyBlocksOverride: false
        });
        setShowConfirmationModal(true);
        return;
      }
      
      // Lender does NOT allow override - check user bypass permission
      if (canBypassAutoDecline) {
        // User has bypass permission - they can override even restricted lenders
        console.log('⚠️ Auto-decline detected - lender blocks override but user has bypass permission');
        setConfirmationData({
          lenderId,
          lenderName: lender.name,
          submissionType,
          matchData,
          isAutoDeclineBypass: true,
          lenderNormallyBlocksOverride: true  // Show extra warning
        });
        setShowConfirmationModal(true);
        return;
      } else {
        // Lender doesn't allow override AND user doesn't have bypass - completely blocked
        console.log('🚫 Auto-decline detected - lender does not allow override and user cannot bypass');
        notifyError(
          `Submission blocked: This deal does not meet required criteria for ${lender.name}. This lender does not allow auto-decline overrides.`, 
          'Auto-Decline - No Override Allowed', 
          5000
        );
        return;
      }
    }
    
    // Check if lender requires confirmation (for non-auto-decline cases)
    // Use preloaded lender data (no API call needed)
    if (lender.requireConfirmation) {
      console.log(`🔍 Confirmation required for lender: ${lender.name} (${lenderId})`);
      
      // Set confirmation data and show modal
      setConfirmationData({
        lenderId,
        lenderName: lender.name,
        submissionType,
        matchData,
        isAutoDeclineBypass: false
      });
      
      console.log('🔒 Match data set for confirmation modal');
      setShowConfirmationModal(true);
      return;
    }
    
    // Proceed with normal submission
    await performSubmission(lenderId, submissionType);
  };

  /**
   * Perform the actual submission to a lender
   * 
   * @param {string} lenderId - ID of the lender to submit to
   * @param {string} [submissionType='api'] - Type of submission ('api' or 'email')
   */
  const performSubmission = async (lenderId, submissionType = 'api') => {
    if (!isLenderVisible(lenderId)) {
      notifyError('This lender is unavailable until its required criteria are met.', 'Submission unavailable', 5000);
      return;
    }
    // Create a unique key for this lender submission based on type
    const lenderKey = submissionType === 'email' ? `email-${lenderId}` : lenderId;
    
    // Clear previous results and errors for this lender
    setResults(prev => ({ ...prev, [lenderKey]: null }));
    setErrors(prev => ({ ...prev, [lenderKey]: null }));
    setErrorDetails(prev => ({ ...prev, [lenderKey]: null }));
    
    // Get lender info
    const lender = lenders.find(l => l.id === lenderId);
    
    // Safety check - if we can't find the lender, log an error and return
    if (!lender) {
      console.error(`No lender found with ID: ${lenderId}`);
      notifyError(`Error: Lender with ID "${lenderId}" not found`, 'Submission Failed', 5000);
      return;
    }
    
    // Mark this lender as submitting
    setSubmittingLenders(prev => ({ ...prev, [lenderKey]: true }));
    
    // Show in-progress notification
    info(
      `Submitting to ${lender.name}${submissionType === 'email' ? ' via email' : ''}...`,
      'Submission Started',
      3000
    );
    
    try {
      // For backward compatibility
      setSubmitting(true);
      setCurrentLender(lender);
      
      // Check if required files are provided based on financing type
      if (financingType === 'equipment-financing') {
        // For equipment financing: need application, quotes, and credit files
        if (appFiles.length === 0 || quoteFiles.length === 0 || creditFiles.length === 0) {
          throw new Error('Application documents, quotes, and credit information are required for equipment financing.');
        }
      } else if (financingType === 'real-estate') {
        // For real estate: need application and asset form files
        if (appFiles.length === 0 || assetFormFiles.length === 0) {
          throw new Error('Application documents and asset form are required for real estate.');
        }
      } else if (financingType === 'sba') {
        // For SBA: need bank statements and application files (same as working capital)
        if (bankFiles.length === 0 || appFiles.length === 0) {
          throw new Error('Bank statements and application documents are required for SBA.');
        }
      } else {
        // For working capital: need bank statements and application files
        if (bankFiles.length === 0 || appFiles.length === 0) {
          throw new Error('Bank statements and application documents are required for working capital.');
        }
      }
      
      // Check if equipment items are provided for equipment financing
      if (financingType === 'equipment-financing' && equipmentItems.length === 0) {
        throw new Error('At least one equipment item is required for equipment financing.');
      }
      
      // Create a copy of the business data and ensure the opportunity ID is included
      const enhancedBusinessData = { 
        ...businessData,
        // Add the opportunity ID explicitly to ensure it's available 
        // for the Salesforce submission
        OpportunityId: opportunityId,
        // Add financing type to the submission data
        FinancingType: financingType,
        // Add equipment items for equipment financing
        ...(financingType === 'equipment-financing' ? { EquipmentItems: equipmentItems } : {})
      };
      
      console.log(`Enhanced business data with opportunity ID from URL: ${opportunityId}, financingType: ${financingType}`);
      
      // Ensure we have valid contact data - create from business data if missing
      let validContactData = contactData;
      
      // If contactData is null/empty or missing essential fields, create it from business data
      if (!validContactData || !validContactData.FirstName || !validContactData.LastName) {
        console.log('Contact data is missing or incomplete, creating from business data...');
        validContactData = {
          FirstName: enhancedBusinessData.ContactFirstName || '',
          LastName: enhancedBusinessData.ContactLastName || '',
          Email: enhancedBusinessData.ContactEmail || '',
          Phone: enhancedBusinessData.ContactCellPhone || '',
          Title: enhancedBusinessData.ContactTitle || 'Owner',
          // Add other contact fields that might be needed
          MobilePhone: enhancedBusinessData.ContactCellPhone || '',
          // Include the original contactData properties if they exist
          ...(contactData || {})
        };
        console.log('Created contact data from business data:', validContactData);
      }
      
      // Submit to the lender API with appropriate files based on financing type
      const additionalFiles =
        financingType === 'equipment-financing'
          ? { quoteFiles, creditFiles }
          : financingType === 'real-estate'
            ? { assetFormFiles }
            : {};

      const response = await submitToLender(
        validContactData,
        enhancedBusinessData,
        lenderId,
        bankFiles,
        appFiles,
        financingType,
        submissionType,
        additionalFiles
      );
      
      // Store the successful result for this lender
      setResults(prev => ({ ...prev, [lenderKey]: response }));
      
      // For backward compatibility - only update result state for API submissions
      if (submissionType === 'api') {
        setResult(response);
      }
      
      // Show success notification with detailed message if available
      const notificationMessage = response.message || `Successfully submitted to ${lender.name}${submissionType === 'email' ? ' via email' : ''}`;
      const notificationTitle = response.isWarning ? 'Warning' : 'Success';
      
      if (response.isWarning) {
        warning(notificationMessage, notificationTitle, 8000);
      } else {
        success(notificationMessage, notificationTitle, 8000);
      }
      
      // Save equipment items to localStorage after successful submission
      if (financingType === 'equipment-financing') {
        saveEquipmentItemsToLocalStorage(equipmentItems);
      }
    } catch (err) {
      console.error(`Error submitting to ${lender.name}:`, err);
      
      // Format error message - Extract detailed information from the error response
      let errorMessage = 'An error occurred during submission';
      let detailedError = null;
      let isBusinessRuleDecline = false;
      let lenderErrorDetails = null;
      
      // Check if there's a response object with data
      if (err.response) {
        console.log('Error response:', err.response);
        
        // Get status code
        const statusCode = err.response.status;
        
        // Try to get error details from response data
        if (err.response.data) {
          const responseData = err.response.data;

          if (responseData.code === 'SF_FILE_SIZE_LIMIT_EXCEEDED' && responseData.limitDetails) {
            lenderErrorDetails = responseData.limitDetails;
          }
          
          // Use the specific message if available
          if (responseData.message) {
            errorMessage = responseData.message;
            
            // Check if this is a business rule decline (422 status with decline-related messages)
            if (statusCode === 422 && (
              errorMessage.includes('decline') ||
              errorMessage.includes('not supported') ||
              errorMessage.includes('not currently supported') ||
              errorMessage.includes('requirements not met') ||
              errorMessage.includes('ineligible') ||
              errorMessage.includes('restricted')
            )) {
              isBusinessRuleDecline = true;
            }
          }
          
          // Check for detailed validation errors (like from PIRS Capital)
          if (responseData.error && typeof responseData.error === 'object') {
            // Extract modelState errors for PIRS Capital
            if (responseData.error.modelState) {
              const modelStateErrors = [];
              
              // Collect all validation errors
              Object.entries(responseData.error.modelState).forEach(([field, errors]) => {
                if (Array.isArray(errors)) {
                  errors.forEach(err => modelStateErrors.push(`${field.replace(/^.*\./, '')}: ${err}`));
                }
              });
              
              if (modelStateErrors.length > 0) {
                detailedError = modelStateErrors.join(', ');
              }
            }
          }
        }
      } else if (err.message) {
        // Use error message directly if no response object
        errorMessage = err.message;
      }
      
      const finalErrorMessage = detailedError ? `${errorMessage}. ${detailedError}` : errorMessage;
      
      // Store the error for this lender with business rule decline flag
      setErrors(prev => ({ 
        ...prev, 
        [lenderKey]: finalErrorMessage,
        [`${lenderKey}_isDecline`]: isBusinessRuleDecline 
      }));

      setErrorDetails(prev => ({
        ...prev,
        [lenderKey]: lenderErrorDetails
      }));
      
      // For backward compatibility - only update error state for API submissions
      if (submissionType === 'api') {
        setError(finalErrorMessage);
      }
      
      // Format the error message for notification
      const formatNotificationError = (message) => {
        // For modelState validation errors, make them more readable
        if (message.includes('Validation error:')) {
          const [prefix, details] = message.split('Validation error:');
          return `${prefix.trim()} Validation Error: ${details.trim()}`;
        }
        return message;
      };
      
      // Determine notification title based on error type
      const notificationTitle = isBusinessRuleDecline ? 'Warning' : 'Submission Failed';
      
      // Show error notification with appropriate title
      notifyError(
        formatNotificationError(`${isBusinessRuleDecline ? 'Notice' : 'Error'} submitting to ${lender.name}${submissionType === 'email' ? ' via email' : ''}: ${finalErrorMessage}`),
        notificationTitle,
        8000
      );
    } finally {
      // Mark this lender as no longer submitting
      setSubmittingLenders(prev => {
        const updated = { ...prev };
        delete updated[lenderKey];
        
        // For backward compatibility - only set submitting to false if this was the last active submission
        if (Object.keys(updated).length === 0) {
          setTimeout(() => setSubmitting(false), 0);
        }
        
        return updated;
      });
    }
  };
  


  // Validate equipment items for equipment financing
  const validateEquipmentItems = () => {
    if (financingType !== 'equipment-financing') return true;
    
    // Check if there are any equipment items
    if (equipmentItems.length === 0) {
      return false;
    }
    
    // Check each equipment item for required fields
    return equipmentItems.every(item => 
      item.equipmentType && 
      item.numberOfUnits && 
      item.costPerUnit
    );
  };
  
  // Check if equipment items are valid
  const equipmentItemsValid = validateEquipmentItems();
  
  return (
    <>
      {/* Equipment Information Section - Only show for equipment financing */}
      {financingType === 'equipment-financing' && (
        <div className="mb-4">
          <EquipmentInformation 
            equipmentItems={equipmentItems}
            setEquipmentItems={setEquipmentItems}
            disabled={disabled}
          />
          
          {/* Equipment validation warning */}
          {!equipmentItemsValid && (
            <Alert variant="warning" className="mt-3 mb-0">
              <FaExclamationTriangle className="me-1" size="0.8em" />
              Please add at least one equipment item with all required details before submitting to lenders.
            </Alert>
          )}
        </div>
      )}
      
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          {/* Debug info */}
          {console.log(`LenderSubmission render - lendersLoading: ${lendersLoading}, lenders.length: ${lenders.length}, preloadedLenders.length: ${preloadedLenders.length}`)}
          
          {/* Loading state for lenders */}
          {lendersLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 mb-0">Loading available lenders...</p>
            </div>
          )}
          
                  {/* No lenders available message - only show if loading is complete and we've processed the preloaded data */}
        {!lendersLoading && lenders.length === 0 && preloadedLenders.length === 0 && (
          <div className="text-center py-4">
            <Alert variant="info">
              <FaExclamationTriangle className="me-2" />
              No active lenders found for {financingType.replace('-', ' ')}. 
              Please check the Lender Settings page to activate lenders.
            </Alert>
          </div>
        )}
          
          {/* Show lender interface only when lenders are loaded */}
          {!lendersLoading && lenders.length > 0 && (
            <>
              {!eligibilityLoaded && <Alert variant="info">Loading lender eligibility. Refresh the page if this message persists.</Alert>}
              {eligibilityLoaded && !lenders.some(lender => isLenderVisible(lender.id)) && (
                <Alert variant="info">No lenders currently qualify for this deal. Complete or update the deal information to check eligibility.</Alert>
              )}
              {/* Optional: Match recalculation button when advanced criteria is enabled */}
              {advancedCriteriaEnabled && !loadingMatch && lastScoreUpdate && (
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-inline-flex align-items-center py-1 px-2"
                    onClick={forceRecalculateScores}
                    disabled={loadingMatch}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <FaSyncAlt size="0.8em" className={loadingMatch ? "spin-animation me-1" : "me-1"} />
                    {loadingMatch ? "Updating..." : "Refresh Match Scores"}
                  </Button>
                </div>
              )}
              
              {/* Tier-Based Lender Submissions */}
          {['1A', '1', '1B', '2', '3'].map(tier => {
            const tierLenders = lenders.filter(lender => String(lender.tier) === tier && isLenderVisible(lender.id));
            
            // Only show the tier section if there are lenders in it
            if (tierLenders.length === 0) return null;
            
            const tierLabels = {
              '1A': { name: "Tier 1A Lenders", description: "Top priority lenders", variant: "success" },
              '1': { name: "Tier 1 Lenders (Starting Point)", description: "Primary lenders - highest priority", variant: "success" },
              '1B': { name: "Tier 1B Lenders", description: "High priority lenders", variant: "success" },
              '2': { name: "Tier 2 Lenders", description: "Secondary lenders - medium priority", variant: "warning" },
              '3': { name: "Tier 3 Lenders", description: "Backup lenders - lowest priority", variant: "secondary" }
            };
            
            return (
              <Card key={tier} className="mb-4 border-0 shadow-sm">
                <Card.Header className={`bg-light text-muted py-2 border-${tierLabels[tier].variant}`}>
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 fs-6 d-flex align-items-center">
                      <span className={`badge bg-${tierLabels[tier].variant} me-2`}>{tier}</span>
                      {tierLabels[tier].name}
                    </h5>
                    <small className="text-muted">{tierLabels[tier].description}</small>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row xs={1} sm={2} md={3} lg={4} className="g-3">
                    {tierLenders.map(lender => {
                      // Determine submission type and result/error key
                      const submissionType = lender.emailOnly ? 'email' : 'api';
                      const lenderKey = lender.emailOnly ? `email-${lender.id}` : lender.id;
                      
                      // Get match data for this lender - prioritize advanced criteria when enabled
                      const lenderScore = getEffectiveMatchScore(lender.id);
                      
                      // Get auto-decline status for this lender
                      const autoDeclineInfo = autoDeclineStatus[lender.id] || { isAutoDecline: false, lenderAllowsOverride: true };
                      
                      // Determine if button should be blocked for auto-decline:
                      // Block if: auto-decline AND (lender doesn't allow override AND user doesn't have bypass)
                      // In other words:
                      //   - User with bypass = never blocked (can override any lender)
                      //   - User without bypass + lender allows override = not blocked (can see warning modal)
                      //   - User without bypass + lender doesn't allow override = blocked (completely blocked)
                      const isBlocked = autoDeclineInfo.isAutoDecline && 
                                        !autoDeclineInfo.lenderAllowsOverride && 
                                        !canBypassAutoDecline;
                      
                      return (
                        <Col key={lenderKey}>
                          <div className="d-flex flex-column h-100">
                            <EnhancedLenderButton
                              lender={lender}
                              matchData={lenderScore}
                              isSubmitting={submittingLenders[lenderKey]}
                              disabled={disabled || (financingType === 'equipment-financing' && !equipmentItemsValid) || isBlocked}
                              onSubmit={() => handleSubmitToLender(lender.id, submissionType)}
                              submissionType={submissionType}
                              variant={submissionType === 'email' ? "outline-secondary" : "outline-primary"}
                              tier={tier}
                              autoDecline={autoDeclineInfo.isAutoDecline}
                              lenderAllowsOverride={autoDeclineInfo.lenderAllowsOverride}
                              canBypassAutoDecline={canBypassAutoDecline}
                            />
                            
                            {/* Display result for this lender */}
                            {results[lenderKey] && (
                              <Alert variant={results[lenderKey].isWarning ? "warning" : "success"} className="p-2 small mb-0">
                                {results[lenderKey].isWarning ? (
                                  <FaExclamationTriangle className="me-1" size="0.8em" />
                                ) : (
                                  <FaCheck className="me-1" size="0.8em" />
                                )}
                                {/* Use the detailed message from server if available, otherwise fallback to default */}
                                {results[lenderKey].message || (submissionType === 'email' ? 'Email sent successfully' : 'Submitted successfully')}
                                {results[lenderKey].submissionId && !results[lenderKey].message?.includes('ID:') && (
                                  <div className="mt-1 text-muted" style={{ fontSize: '0.9em' }}>
                                    ID: <strong>{results[lenderKey].submissionId}</strong>
                                  </div>
                                )}
                              </Alert>
                            )}
                            
                            {/* Display error for this lender */}
                            {errors[lenderKey] && (
                              <Alert variant={errors[`${lenderKey}_isDecline`] ? "warning" : "danger"} className="p-2 small mb-0">
                                <FaExclamationTriangle className="me-1" size="0.8em" />
                                <div>
                                  <div><strong>{errors[`${lenderKey}_isDecline`] ? 'Warning:' : (submissionType === 'email' ? 'Email Failed:' : 'Submission Failed:')}</strong></div>
                                  <div>
                                    {/* Format error message for better readability */}
                                    {errors[lenderKey].split('. ').map((part, index) => (
                                      <p key={index} className={index > 0 ? 'mt-1 mb-0' : 'mb-0'}>
                                        {/* Highlight validation errors */}
                                        {part.includes(':') && part.includes('Validation error') ? (
                                          <>
                                            <span>{part.split(':')[0]}: </span>
                                            <strong className="text-danger">{part.split(':').slice(1).join(':')}</strong>
                                          </>
                                        ) : (
                                          part
                                        )}
                                      </p>
                                    ))}
                                  </div>
                                  {errorDetails[lenderKey] && (
                                    <div className="mt-2">
                                      <div><strong>File size limits</strong></div>
                                      <div className="mb-1">
                                        Per-file max: {errorDetails[lenderKey].perFileLimitMB}MB | Total max: {errorDetails[lenderKey].totalLimitMB}MB | Selected total: {errorDetails[lenderKey].totalSelectedSizeLabel}
                                      </div>
                                      {Array.isArray(errorDetails[lenderKey].oversizedFiles) && errorDetails[lenderKey].oversizedFiles.length > 0 && (
                                        <ul className="mb-0 ps-3">
                                          {errorDetails[lenderKey].oversizedFiles.map((file, idx) => (
                                            <li key={`${file.name}-${idx}`}>
                                              {file.name} ({file.sizeLabel})
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </Alert>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>
            );
                      })}
          
            {/* Backward compatibility display */}
            {(result || error) && !Object.keys(results).length && !Object.keys(errors).length && (
              <Alert variant={error ? 'danger' : 'success'} className="mt-3">
                {error ? (
                  <>
                    <FaExclamationTriangle className="me-2" />
                    {error}
                  </>
                ) : (
                  <>
                    <FaCheck className="me-2" />
                    Successfully submitted to {currentLender?.name}.
                    {result.submissionId && (
                      <div className="mt-1">
                        Submission ID: <strong>{result.submissionId}</strong>
                      </div>
                    )}
                  </>
                )}
              </Alert>
            )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      {confirmationData && (
        <ConfirmationModal
          show={showConfirmationModal}
          onHide={handleCancelConfirmation}
          onConfirm={handleConfirmSubmission}
          lenderName={confirmationData.lenderName}
          matchedCriteria={confirmationData.matchData?.matched || []}
          missedCriteria={confirmationData.matchData?.missed || []}
          incompleteCriteria={confirmationData.matchData?.incomplete || []}
          isSubmitting={submittingLenders[confirmationData.submissionType === 'email' ? `email-${confirmationData.lenderId}` : confirmationData.lenderId]}
          hasDisqualifyingCriteria={confirmationData.matchData?.disqualified || false}
          advancedCriteriaResults={confirmationData.matchData?.advancedCriteriaResults || null}
          isAutoDeclineBypass={confirmationData.isAutoDeclineBypass || false}
          lenderNormallyBlocksOverride={confirmationData.lenderNormallyBlocksOverride || false}
        />
      )}
    </>
  );
};

export default LenderSubmission;
